import type { ReactNode } from "react";
import { Box, Chip, Divider, Paper, Stack, Tooltip, Typography } from "@mui/material";
import {
  Business,
  MedicalServices,
  Place,
  WarningAmber,
} from "@mui/icons-material";
import dayjs, { type Dayjs } from "dayjs";
import { isEvolutionAppointment } from "@/components/schedule/evolution/isEvolutionAppointment";
import type { TeleconsultoriaReceivedInvite } from "../../types";
import type { ReceivedInviteTone } from "../../utils/groupReceivedInvites";
import { TeleconsultoriaJoinCallButton } from "../TeleconsultoriaJoinCallButton";
import { INVITE_TONE_BORDER } from "./inviteTone";

interface ReceivedInviteCardProps {
  invite: TeleconsultoriaReceivedInvite;
  tone: ReceivedInviteTone;
  now: Dayjs;
}

/** "Começa em X min" / "Iniciado há X min" para o grupo "Acontecendo agora". */
function relativeStartLabel(start: string, now: Dayjs): string {
  const diff = dayjs(start).diff(now, "minute");
  if (diff > 0) return `Começa em ${diff} min`;
  if (diff === 0) return "Começando agora";
  const ago = Math.abs(diff);
  if (ago < 60) return `Iniciado há ${ago} min`;
  return `Iniciado há ${Math.floor(ago / 60)}h${String(ago % 60).padStart(2, "0")}`;
}

/** Metadado com ícone; texto longo corta e o tooltip mostra rótulo + texto completo. */
function MetaItem({ icon, label, text }: { icon: ReactNode; label: string; text: string }) {
  return (
    <Tooltip title={`${label}: ${text}`}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: 260 }}>
        <Box aria-hidden sx={{ display: "flex", color: "text.secondary" }}>
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary" noWrap>
          {text}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

export function ReceivedInviteCard({ invite, tone, now }: ReceivedInviteCardProps) {
  const start = invite.start ? dayjs(invite.start) : undefined;
  const hasStart = Boolean(start?.isValid());
  const isEvolution = isEvolutionAppointment(invite.appointment);

  const meta = [
    { key: "practitioner", label: "Médico solicitante", text: invite.practitionerName, icon: <MedicalServices fontSize="small" /> },
    { key: "location", label: "Local", text: invite.locationName, icon: <Place fontSize="small" /> },
    { key: "organization", label: "Organização", text: invite.organizationName, icon: <Business fontSize="small" /> },
  ].filter((item): item is typeof item & { text: string } => Boolean(item.text));

  return (
    <Paper
      variant="outlined"
      role="listitem"
      sx={{
        p: 1.5,
        borderLeft: "4px solid",
        borderLeftColor: INVITE_TONE_BORDER[tone],
        borderRadius: "0 8px 8px 0",
        // Anteriores ficam esmaecidos: ainda abertos, mas não são prioridade.
        opacity: tone === "past" ? 0.75 : 1,
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: (theme) => theme.shadows[3] },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.5, sm: 2 }}
        alignItems={{ sm: "center" }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {/* Coluna de horário */}
          <Box sx={{ minWidth: 64, textAlign: "center", flexShrink: 0 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              lineHeight={1.2}
              color={tone === "now" ? "success.main" : "text.primary"}
            >
              {hasStart ? start?.format("HH:mm") : "--:--"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasStart ? start?.format("DD/MM") : "Sem data"}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />

          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                {invite.patientName}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={isEvolution ? "secondary" : "primary"}
                label={isEvolution ? "Evolução" : "Consulta"}
              />
              {!invite.patientId && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  icon={<WarningAmber />}
                  label="Paciente não identificado"
                />
              )}
            </Stack>
            {tone === "now" && hasStart && invite.start && (
              <Typography variant="caption" color="success.main" fontWeight={600}>
                {relativeStartLabel(invite.start, now)}
              </Typography>
            )}
            {meta.length > 0 && (
              <Stack direction="row" columnGap={2} rowGap={0.5} flexWrap="wrap" useFlexGap>
                {meta.map((item) => (
                  <MetaItem key={item.key} icon={item.icon} label={item.label} text={item.text} />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>

        {/* Mobile: botão na largura toda, sem mudar as props do componente. */}
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          <TeleconsultoriaJoinCallButton
            roomUrl={invite.roomUrl}
            appointmentId={invite.appointmentId}
            patientName={invite.patientName}
            patientId={invite.patientId}
            appointment={invite.appointment}
            openOnPatientRecords
          />
        </Box>
      </Stack>
    </Paper>
  );
}
